import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '@/lib/api-context'

/** Implantação da clínica (docs/api/admin.md, "Implantação"; RN-ADM-80 a 90). */

export type OnboardingStepKey = 'clinic_profile' | 'witnesses' | 'procedures' | 'review'

/** De onde a clínica veio (RN-ADM-87): painel root, cadastro próprio ou carga do legado. */
export type ClinicSource = 'panel' | 'self_service' | 'legacy'

export interface OnboardingStep {
  key: OnboardingStepKey
  status: 'done' | 'pending'
  confirmedAt: string | null
}

export interface Onboarding {
  isCompleted: boolean
  completedAt: string | null
  completedBy: { id: string; name: string } | null
  /** A gestora adiou a implantação (RN-ADM-90): a porta de entrada deixa de levar a ela. */
  skippedAt: string | null
  source: ClinicSource
  currentStep: OnboardingStepKey | null
  steps: OnboardingStep[]
  summary: {
    procedureCount: number
    pendingCatalogLinkCount: number
    proceduresWithoutConsentFormCount: number
    consentFormCount: number
    witnessCount: number
    clinicSignatureMode: 'manual' | 'automatic'
  }
}

export const STEP_LABELS: Record<OnboardingStepKey, string> = {
  clinic_profile: 'Empresa',
  witnesses: 'Testemunhas',
  procedures: 'Procedimentos',
  review: 'Revisão',
}

export const onboardingKeys = { all: ['onboarding'] as const }

export function useOnboarding(options: { enabled?: boolean } = {}) {
  const api = useApi()
  return useQuery({
    queryKey: onboardingKeys.all,
    queryFn: () => api.get<Onboarding>('/admin/onboarding'),
    enabled: options.enabled ?? true,
    staleTime: 30_000,
  })
}

export function useConfirmOnboardingStep() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (key: 'clinic_profile' | 'witnesses') =>
      api.post<Onboarding>(`/admin/onboarding/steps/${key}/confirm`),
    onSuccess: (data) => queryClient.setQueryData(onboardingKeys.all, data),
  })
}

export function useCompleteOnboarding() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<Onboarding>('/admin/onboarding/complete'),
    onSuccess: (data) => queryClient.setQueryData(onboardingKeys.all, data),
  })
}

/** Adia a implantação (RN-ADM-90): a pessoa continua pelo dashboard, etapa a etapa. */
export function useSkipOnboarding() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<Onboarding>('/admin/onboarding/skip'),
    onSuccess: (data) => queryClient.setQueryData(onboardingKeys.all, data),
  })
}

/** A porta de entrada leva à implantação enquanto ela não foi concluída nem adiada (RN-ADM-80, 90). */
export function shouldOpenOnboarding(onboarding: Onboarding | undefined): boolean {
  return Boolean(onboarding && !onboarding.isCompleted && !onboarding.skippedAt)
}

/** Quantas etapas faltam, para o contador da sidebar. */
export function pendingStepCount(onboarding: Onboarding | undefined): number {
  if (!onboarding || onboarding.isCompleted) return 0
  return onboarding.steps.filter((step) => step.status === 'pending').length
}
